import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, PlusCircle, FilterIcon, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { employeesColumns, columnsExcel } from '@/pages/parametrizacion/employees/utils/employeesColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchEmployees, createEmployee, updateEmployee, deleteEmployee, exportEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const EmployeesPage = () => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [plantasOptions, setPlantasOptions] = useState([]);
  const [areasOptions, setAreasOptions] = useState([]);
  const [centrosCostoOptions, setCentrosCostoOptions] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Cargar plantas para los filtros
  const loadPlantas = useCallback(async () => {
    try {
      const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
      const response = await plantasService.getAll();
      const plantas = response?.data || [];
      setPlantasOptions(plantas.map(planta => ({
        value: planta._id || planta.id,
        label: planta.nombre
      })));
    } catch (error) {
      console.error('Error cargando plantas:', error);
    }
  }, []);

  // Cargar áreas para los filtros
  const loadAreas = useCallback(async () => {
    try {
      const { areasService } = await import('@/pages/parametrizacion/areas/utils/areasService');
      const response = await areasService.getAll();
      const areas = response?.data || [];
      setAreasOptions(areas.map(area => ({
        value: area._id || area.id,
        label: area.nombre
      })));
    } catch (error) {
      console.error('Error cargando áreas:', error);
    }
  }, []);

  // Cargar centros de costo para los filtros
  const loadCentrosCosto = useCallback(async () => {
    try {
      const { centrosCostoService } = await import('@/pages/parametrizacion/centrosCosto/utils/centrosCostoService');
      const response = await centrosCostoService.getAll();
      const centrosCosto = response?.data || [];
      setCentrosCostoOptions(centrosCosto.map(centroCosto => ({
        value: centroCosto._id || centroCosto.id,
        label: centroCosto.nombre
      })));
    } catch (error) {
      console.error('Error cargando centros de costo:', error);
    }
  }, []);

  const fetchAndSetEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const query = { ...filters };
      if (quickSearch.trim()) {
        query.$or = [
          { nombre: { $regex: quickSearch.trim(), $options: 'i' } },
          { apellido: { $regex: quickSearch.trim(), $options: 'i' } },
          { cedula: { $regex: quickSearch.trim(), $options: 'i' } },
        ];
      }
      const { data, totalRecords } = await fetchEmployees({ limit: ITEMS_PER_PAGE, offset, query });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener los empleados.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, quickSearch, toast]);

  useEffect(() => {
    fetchAndSetEmployees();
    loadPlantas();
    loadAreas();
    loadCentrosCosto();
  }, [fetchAndSetEmployees, loadPlantas, loadAreas, loadCentrosCosto]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (mode, item = null) => {
    // Verificar que las opciones estén cargadas antes de abrir el modal
    if (plantasOptions.length === 0 || areasOptions.length === 0 || centrosCostoOptions.length === 0) {
      toast({
        title: 'Cargando...',
        description: 'Espera mientras se cargan las opciones necesarias.',
        variant: 'default'
      });
      return;
    }

    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        await createEmployee(itemData);
        toast({ title: "Éxito", description: "Empleado creado correctamente." });
      } else if (modalMode === 'edit') {
        await updateEmployee(currentItem._id, itemData);
        toast({ title: "Éxito", description: "Empleado actualizado correctamente." });
      }
      handleCloseModal();
      fetchAndSetEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      const errorMessage = error.response?.data?.message || error.message || "Ocurrió un problema al guardar el empleado.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteEmployee(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: "Empleado eliminado", description: "El empleado fue eliminado correctamente." });
        } else {
          throw new Error(response.message || "Error al eliminar empleado");
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar el empleado.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAndSetEmployees();
    }
  };

  // Crear columnas con opciones pre-cargadas
  const getEmployeesColumnsWithOptions = () => {
    return employeesColumns.map(column => {
      if (column.id === 'planta') {
        return {
          ...column,
          options: plantasOptions.map(planta => ({
            value: { id: planta.value, nombre: planta.label },
            label: planta.label
          }))
        };
      }
      if (column.id === 'area') {
        return {
          ...column,
          options: areasOptions.map(area => ({
            value: { id: area.value, nombre: area.label },
            label: area.label
          }))
        };
      }
      if (column.id === 'centroCosto') {
        return {
          ...column,
          options: centrosCostoOptions.map(centroCosto => ({
            value: { id: centroCosto.value, nombre: centroCosto.label },
            label: centroCosto.label
          }))
        };
      }
      return column;
    });
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'edit') {
      handleOpenModal('edit', row);
    } else if (actionType === 'delete') {
      handleDeleteConfirmation(row);
    } else if (actionType === 'view') {
      toast({ title: "Ver Empleado", description: `Visualizando empleado: ${row.nombre}` });
    }
  };


  const handleFilterChange = (filters) => {
    setFilters(filters);
    setIsDrawerOpen(false);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="w-full md:w-auto">
            <div className="flex items-center space-x-3 mb-2">
              <ShoppingCart className="h-8 w-20 text-primary" />
              <CardTitle className="text-2xl">Parametrización de Empleados</CardTitle>
            </div>
            <CardDescription>Gestiona los empleados de la empresa.</CardDescription>
          </div>

          <div className="w-full flex flex-col gap-3">

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por nombre o documento..."
                value={quickSearch}
                onChange={(e) => { setQuickSearch(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-9"
              />
              {quickSearch && (
                <button
                  onClick={() => { setQuickSearch(''); setCurrentPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsDrawerOpen(true)} >
                <FilterIcon className="mr-2 h-4 w-4" variant="outline" size={20} />
                Filtros
              </Button>
              <ExportExcelButton
                fileName="Empleados"
                columns={columnsExcel}
                filters={filters}
                service={exportEmployees}
              />
              <Button onClick={() => handleOpenModal('create')}>
                <PlusCircle className="mr-2 h-4 w-4" /> Crear Empleado
              </Button>

            </div>
          </div>

        </CardHeader>
        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={[
                {
                  id: 'estado', label: 'Estado', type: 'select', options: [
                    { value: 'Activo', label: 'Activo' },
                    { value: 'Inactivo', label: 'Inactivo' },
                  ]
                },
                {
                  id: 'planta.id',
                  label: 'Planta',
                  type: 'select',
                  options: plantasOptions
                },
                {
                  id: 'area.id',
                  label: 'Área',
                  type: 'select',
                  options: areasOptions
                },
                {
                  id: 'centroCosto.id',
                  label: 'Centro de Costos',
                  type: 'select',
                  options: centrosCostoOptions
                },
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <DataTable
            data={displayedData}
            columns={employeesColumns}
            isLoading={isLoading}
            onAction={handleAction}
            showCreateButton={false}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado', label: 'Estado', type: 'select', options: [
                  { value: 'Activo', label: 'Activo' },
                  { value: 'Inactivo', label: 'Inactivo' },
                ]
              },
              {
                id: 'planta.id',
                label: 'Planta',
                type: 'select',
                options: plantasOptions
              },
              {
                id: 'area.id',
                label: 'Área',
                type: 'select',
                options: areasOptions
              },
              {
                id: 'centroCosto.id',
                label: 'Centro de Costos',
                type: 'select',
                options: centrosCostoOptions
              },
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && plantasOptions.length > 0 && areasOptions.length > 0 && centrosCostoOptions.length > 0 && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={getEmployeesColumnsWithOptions()}
            title={modalMode === 'create' ? 'Crear Nuevo Empleado' : 'Editar Empleado'}
            mode={modalMode}
            twoColumns={true}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este empleado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el empleado
              "{itemToDelete?.nombre}" de tus registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default EmployeesPage;